import { memo, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useNotificationCenter } from "@/contexts/NotificationContext";
import styles from "./Navbar.module.scss";
import {
  BellNavIcon,
  CalendarNavIcon,
  ChatNavIcon,
  ChevronDownIcon,
  DashboardNavIcon,
  HomeNavIcon,
  ProfileNavIcon,
} from "@/components/SVGComponents/MemberIcons";
import { clear } from "@/store/slices/globalSlice";
import { captalizeEachWord, clearOnLogout, getCurrentUser, getFullName } from "@/lib/utils";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";
import routes from "@/routes/routes";
import AuthLogo from "@/pages/Auth/components/AuthLogo";
import BFCSignUP from "@/assets/svg/LoginPage/BFCSignUP";
import RTILogo from "@/assets/png/RTILogo.png";

export interface SelectOption {
  label: string;
  value: string;
}

const NAV_ITEMS = [
  {
    label: "Home",
    path: routes.privateRoutes.HOME.path,
    icon: HomeNavIcon,
  },
  {
    label: "Dashboard",
    path: routes.privateRoutes.DASHBOARD.path,
    icon: DashboardNavIcon,
  },
  // {
  //   label: "Chat",
  //   path: routes.privateRoutes.CHAT.path,
  //   icon: ChatNavIcon,
  // },
  // {
  //   label: "Event",
  //   path: routes.privateRoutes.EVENT.path,
  //   icon: CalendarNavIcon,
  // },
  {
    label: "Profile",
    path: routes.privateRoutes.PROFILE.path,
    icon: ProfileNavIcon,
  },
] as const;

const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notificationCount, isNotificationOpen, openNotifications } =
    useNotificationCenter();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const user = useMemo(() => getCurrentUser() || {}, []);
  const fullName = useMemo(() => getFullName() || "", []);
  const isAdmin = useMemo(() => Boolean(user?.isAdmin || user?.role === "ADMIN"), [user]);

  const navItems = useMemo(() => {
    if (isAdmin) {
      return [
        {
          label: "User Management",
          path: "/admin/users-management",
          icon: DashboardNavIcon,
        },
      ];
    }
    return NAV_ITEMS;
  }, [isAdmin]);

  const displayName = captalizeEachWord(
    fullName ||
      user?.userName ||
      user?.name ||
      user?.email?.split("@")?.[0]?.replace(/\./g, " ") ||
      (isAdmin ? "Admin" : "Member"),
  );
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase())
    .join("");

  const userEmail = useMemo(() => user?.email || "", [user]);
  const userRole = useMemo(() => {
    if (user?.role) return String(user.role).replace("ROLE_", "").replace(/_/g, " ");
    if (user?.permissions?.[0]) return String(user.permissions[0]).replace("ROLE_", "").replace(/_/g, " ");
    return isAdmin ? "ADMIN" : "MEMBER";
  }, [user, isAdmin]);

  const handleLogout = () => {
    const isUserAdmin = isAdmin || window.location.pathname.startsWith("/admin");
    clearOnLogout();
    dispatch(clear());
    navigate(isUserAdmin ? AUTH_ROUTE_PATHS.adminLogin : AUTH_ROUTE_PATHS.signIn);
  };

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  return (
    <header className={styles.topBarContainer}>
      <div className={styles.logoBlock} aria-label="RTI logo">
        <img alt="Round Table India" className={styles.logo} src={RTILogo} />
      </div>

      <nav className={styles.navigation}>
        {navItems.map(({ icon: Icon, label, path }) => (
          <NavLink
            key={label}
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
            }
            to={path}
          >
            <Icon className={styles.navIcon} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className={styles.actions} ref={dropdownRef}>
        <button
          className={styles.profileButton}
          onClick={() => setIsOpen((previous) => !previous)}
          type="button"
        >
          <span className={styles.avatar}>{initials || "AD"}</span>
          <span className={styles.userName}>{displayName}</span>
          <ChevronDownIcon
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
          />
        </button>

        {isOpen ? (
          <div className={styles.dropdown}>
            <div className={styles.dropdownHeader}>
              <span className={styles.dropdownAvatar}>{initials || "AD"}</span>
              <div className={styles.dropdownUserMeta}>
                <span className={styles.dropdownName}>{displayName}</span>
                {userEmail ? (
                  <span className={styles.dropdownEmail}>{userEmail}</span>
                ) : null}
                {userRole ? (
                  <span className={styles.dropdownRoleTag}>{userRole}</span>
                ) : null}
              </div>
            </div>

            <div className={styles.dropdownDivider} />

            {!isAdmin && (
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setIsOpen(false);
                  navigate(routes.privateRoutes.PROFILE.path);
                }}
                type="button"
              >
                Profile
              </button>
            )}

            <button
              className={`${styles.dropdownItem} ${styles.logoutButton}`}
              onClick={handleLogout}
              type="button"
            >
              Logout
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default memo(Navbar);
